import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, TextInput, Button, Flex } from '@tremor/react';
import { api } from '../../lib/api';

interface FlushQueuesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function FlushQueuesModal({ isOpen, onClose, onSuccess }: FlushQueuesModalProps) {
  const [confirmation, setConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    totalKeys: number;
    prefix: string;
    summary: Record<string, number>;
  } | null>(null);

  // Load preview when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmation('');
      setError(null);
      loadPreview();
    }
  }, [isOpen]);

  const loadPreview = async () => {
    setIsLoadingPreview(true);
    try {
      const data = await api.flushQueuesPreview();
      setPreview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleFlush = async () => {
    if (confirmation !== 'FLUSH') {
      setError('You must type FLUSH to confirm');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await api.flushQueues('FLUSH');
      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError('Flush failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to flush queues');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setConfirmation('');
      setError(null);
      setPreview(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} static={true}>
      <DialogPanel className="bg-gray-900 border border-red-900 max-w-lg">
        {/* Warning Header */}
        <div className="bg-red-950/50 border border-red-900 rounded-lg p-4 mb-4">
          <div className="text-center mb-2">
            <span className="text-2xl">&#9888;</span>
          </div>
          <Title className="text-red-400 text-center">DANGER: Flush All Queues</Title>
        </div>

        {/* Warning Message */}
        <div className="bg-red-950/30 border border-red-900/50 rounded p-3 mb-4">
          <Text className="text-red-300 font-medium mb-2">
            This will DELETE ALL BullMQ job data including:
          </Text>
          <ul className="text-red-200 text-sm space-y-1 ml-4">
            <li>All queued jobs (waiting, delayed)</li>
            <li>All active jobs (will be orphaned)</li>
            <li>All job history (completed, failed)</li>
          </ul>
          <Text className="text-red-400 font-bold mt-3">
            This action CANNOT be undone!
          </Text>
        </div>

        {/* Preview */}
        {isLoadingPreview ? (
          <div className="bg-gray-800/50 rounded p-3 mb-4">
            <Text className="text-gray-400">Loading preview...</Text>
          </div>
        ) : preview ? (
          <div className="bg-gray-800/50 rounded p-3 mb-4">
            <Flex justifyContent="between" className="mb-2">
              <Text className="text-gray-400">Keys to delete:</Text>
              <Text className="text-red-400 font-bold">{preview.totalKeys.toLocaleString()}</Text>
            </Flex>
            <Text className="text-gray-500 text-xs">Prefix: {preview.prefix}</Text>
            {preview.summary && Object.keys(preview.summary).length > 0 && (
              <div className="mt-2 text-xs text-gray-500">
                {Object.entries(preview.summary)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([type, count]) => (
                    <div key={type}>
                      {type}: {count.toLocaleString()}
                    </div>
                  ))}
              </div>
            )}
          </div>
        ) : null}

        {/* Confirmation Input */}
        <div className="mb-4">
          <Text className="text-gray-300 mb-2">
            Type <span className="text-red-400 font-bold">FLUSH</span> to confirm:
          </Text>
          <TextInput
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="Type FLUSH to confirm"
            className="bg-gray-800 border-gray-700"
            disabled={isLoading}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-950/30 border border-red-900/50 rounded p-2 mb-4">
            <Text className="text-red-400 text-sm">{error}</Text>
          </div>
        )}

        {/* Actions */}
        <Flex justifyContent="end" className="gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleFlush}
            loading={isLoading}
            disabled={confirmation !== 'FLUSH' || isLoading}
          >
            Flush All Queues
          </Button>
        </Flex>
      </DialogPanel>
    </Dialog>
  );
}
